import { useEffect, useCallback } from 'react'
import { useQuery } from 'react-query'
import { useGetGotchiOwnerQuery } from 'vvs-gotchi-graphql'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useGotchiAuth } from 'state/vvsgotchi/hooks/useAuth'
import { useAcceptance } from 'state/vvsgotchi/hooks/useSignAuthAndAcceptance'
import { QueryKey } from 'config/constants/queryKey'

import { Mole, Owner, ownerFromGraphqlData } from '../models'

interface GotchiOwnerReturnType {
  loading: boolean

  /**
   * true when wallet connected & authenticated with backend, ready for interaction
   */
  ready: boolean

  owner?: Owner | null
  team?: Mole[]
  moles?: Mole[]
  isOnboarded: boolean
  refresh: () => Promise<void>
}
const emptyGotchiOwner: Omit<GotchiOwnerReturnType, 'loading' | 'ready' | 'refresh'> = {
  owner: null,
  team: [],
  moles: [],
  isOnboarded: false,
}

const useGotchiOwner = (): GotchiOwnerReturnType => {
  const { account } = useActiveWeb3React()
  const [requireAcceptance] = useAcceptance()
  const [authValid, ensureAuth] = useGotchiAuth()

  const { data, loading, refetch, error } = useGetGotchiOwnerQuery({
    variables: {
      address: account,
    },
    skip: !account || !authValid,
  })

  useEffect(() => {
    if (!account || requireAcceptance) return
    if (!authValid || error) {
      ensureAuth()
        .then(() => {
          refetch()
        })
        .catch(() => undefined)
    }
  }, [error, authValid, requireAcceptance, account, ensureAuth, refetch])

  const { data: gotchiOwner } = useQuery(
    [QueryKey.VVS_GOTCHI_OWNER, account, data],
    () => {
      const owner = data?.owner ? ownerFromGraphqlData(data.owner) : null

      return {
        owner,
        team: selectExpeditionTeam(owner),
        moles: selectUserMoles(owner),
        isOnboarded: selectIsOnboarded(owner),
      }
    },
    { staleTime: Infinity, keepPreviousData: true },
  )

  return {
    loading,
    ready: gotchiOwner?.owner?.address && gotchiOwner.owner.address.toLowerCase() === account.toLowerCase(),
    ...(gotchiOwner || emptyGotchiOwner),
    refresh: useCallback(async () => {
      if (!account) return
      await refetch({ address: account })
    }, [account, refetch]),
  }
}

export default useGotchiOwner

const selectExpeditionTeam = (owner?: Owner) => {
  return owner?.moles.filter((m) => m.isEnlisted)
}
const selectUserMoles = (owner?: Owner) => {
  return owner?.moles
}
const selectIsOnboarded = (owner?: Owner) => {
  return owner?.isOnboarded
}
